import { booleanAttribute, Component, Input, OnDestroy, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonIcon, IonSpinner, NavController } from '@ionic/angular/standalone';

/** Delay (ms) before navigation runs after click. */
const DEFAULT_NAV_DELAY_MS = 500;

/**
 * Dashboard-style tile link: on click, disables for {@link DEFAULT_NAV_DELAY_MS}, replaces the icon
 * with a spinner, then navigates (default delay 0.5s). While busy, border, spinner, and title share
 * one colour: primary on default tiles, primary-contrast on `themePrimary` tiles (solid fill).
 * Re-enable via {@link resetLoadingState} when navigation returns false, or when the parent page
 * calls reset on ionViewDidLeave (preferred: clears after the page is off-screen so back navigation
 * does not flash the busy state) and/or ionViewWillEnter.
 *
 * Uses {@link NavController.navigateForward} so `ion-router-outlet` gets the same stack behaviour
 * as `routerLink` + `routerDirection="forward"` (plain `Router.navigateByUrl` does not).
 */
@Component({
  selector: 'crowdctrl-service-nav-tile',
  standalone: true,
  imports: [CommonModule, IonIcon, IonSpinner],
  templateUrl: './service-nav-tile.component.html',
  styleUrls: ['../../styles/service-tiles.css', './service-nav-tile.component.css'],
  host: {
    '[class.service-nav-tile--expanded-host]': 'expandedCard',
  },
})
export class ServiceNavTileComponent implements OnDestroy {
  private readonly navCtrl = inject(NavController);

  /** App route (e.g. `/about/features`). Used for href and forward navigation. */
  @Input({ required: true }) route!: string;

  @Input({ required: true }) label!: string;

  /** ion-icon name (e.g. `grid-outline`). Register icons in the app shell. */
  @Input({ required: true }) icon!: string;

  /**
   * When true, one full card-style link: icon + label row, then projected content (e.g. summary
   * copy). Used on How it works so the tile is the whole surface without duplicating icon/label.
   */
  @Input({ transform: booleanAttribute }) expandedCard = false;

  /**
   * Solid primary tile: idle border matches the fill only (other tiles keep the default border).
   * While busy, border, label, and spinner share contrast so they read on the fill.
   */
  @Input({ transform: booleanAttribute }) themePrimary = false;

  @Input() navDelayMs = DEFAULT_NAV_DELAY_MS;

  /** When true, the visible label is wrapped in {@code <strong>} (e.g. CrowdCTRL app names on marketing tiles). */
  @Input({ transform: booleanAttribute }) labelStrong = false;

  /**
   * When true, the tile stays visible but does not navigate (muted, not focusable as a link).
   * Use for routes that are temporarily unavailable.
   */
  @Input({ transform: booleanAttribute }) disabled = false;

  busy = false;

  private navigationTimer: ReturnType<typeof setTimeout> | undefined;

  ngOnDestroy(): void {
    this.clearTimer();
  }

  /** Clears loading state and pending navigation timer (e.g. ionViewWillEnter on parent). */
  resetLoadingState(): void {
    this.busy = false;
    this.clearTimer();
  }

  onClick(event: Event): void {
    event.preventDefault();
    if (this.disabled || this.busy) {
      return;
    }
    this.busy = true;
    this.clearTimer();
    this.navigationTimer = setTimeout(() => {
      this.navigationTimer = undefined;
      void this.runNavigate();
    }, this.navDelayMs);
  }

  private async runNavigate(): Promise<void> {
    try {
      const ok = await this.navCtrl.navigateForward(this.route);
      if (!ok) {
        this.resetLoadingState();
      }
    } catch {
      this.resetLoadingState();
    }
  }

  private clearTimer(): void {
    if (this.navigationTimer !== undefined) {
      clearTimeout(this.navigationTimer);
      this.navigationTimer = undefined;
    }
  }
}
